"use client";

import styles from "./page-searchbar.module.css";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";
import { AutocompleteSuggestion } from "@/types/types";
import { CircleFlag } from "react-circle-flags";
import { useListingsCacheStore } from "@/global-cache/useListingsCacheStore";
import { useDropdownPosition } from "@/hooks/use-dropdown-position";
import { useSavedListingsStore } from "../useSavedListingsStore";
import TypeSelector from "./type/type-selector";
import PurposeSelector from "./purpose/purpose-selector";

export default function PageSearchbar({
  locale,
  sessionId,
}: {
  locale: string;
  sessionId: string | null;
}) {
  const t = useTranslations("PagesSearchbars");

  const type = useSavedListingsStore((state) => state.type);
  const purpose = useSavedListingsStore((state) => state.purpose);
  const text = useSavedListingsStore((state) => state.text);
  const onTextChange = useSavedListingsStore((state) => state.onTextChange);
  const refetchState = useSavedListingsStore().refetchState;
  const setCache = useListingsCacheStore((state) => state.setMultipleListings);
  const suggestionsWrapperRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div ref={scrollRef} className={styles.searchbarWrapper}>
      <div
        className={`${styles.itemWrapper} ${styles.inputWrapper}`}
        ref={suggestionsWrapperRef}
        onClick={(e) => {
          if (
            e.target instanceof HTMLElement &&
            (e.target.tagName === "BUTTON" || e.target.closest("button"))
          ) {
            return;
          }

          if (!inputRef.current) return;
          inputRef.current.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "nearest",
          });
          inputRef.current.focus();
        }}
      >
        <div className={styles.item}>
          <div className={styles.textWrapper}>
            <label className={styles.title}>
              {t("savedAndMyListings.searchLabel", { default: "Search" })}
            </label>
            <input
              className={styles.input}
              ref={inputRef}
              type="text"
              value={text}
              placeholder={t("savedAndMyListings.fieldDescription")}
              onChange={(e) => onTextChange(e, locale, sessionId, setCache)}
            />
          </div>
        </div>
      </div>

      <div className={styles.separator}></div>

      <TypeSelector
        t={t}
        type={type}
        refetchState={async () => {
          await refetchState(locale, sessionId, setCache);
        }}
      />

      <div className={styles.separator}></div>

      <PurposeSelector
        t={t}
        purpose={purpose}
        refetchState={async () => {
          await refetchState(locale, sessionId, setCache);
        }}
      />
    </div>
  );
}
